import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  onIdTokenChanged as firebaseOnIdTokenChanged,
  updatePassword as firebaseUpdatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
  User as FirebaseUser,
  ParsedToken,
  IdTokenResult,
} from 'firebase/auth';
import { Users, Users_Select_Column } from '@gql/base-types';
import { GetUserProfileDocument } from '../queries/getUserProfile.query.generated';
import { GetUserProfileUpdatedAtDocument } from '../queries/getUserProfileUpdatedAt.query.generated';
import { UpdateUserActivityDocument } from '../queries/updateUserActivity.mutation.generated';
import app from './firebase.config';
import { execGraphqlWithFetch, HttpResponseError } from '@utils/gql';
import siteConfig from '@config';
import { setPermissions } from '@store';
import { grantedFeatureNames } from './featureGrants';

export interface Claims extends ParsedToken {
  'https://hasura.io/jwt/claims': {
    'x-hasura-default-role'?: string;
    'x-hasura-allowed-customers'?: string;
    'x-hasura-allowed-payers'?: string;
    'x-hasura-payer-child-customers'?: string;
    'x-hasura-allowed-regions'?: string;
    'x-hasura-allowed-roles'?: string;
    'x-hasura-customer-id'?: string;
    'x-hasura-user-id'?: string;
    'x-hasura-user-email'?: string;
  };
}

//Claims after they have been converted into their useable types
export interface ConvertedClaims {
  'x-hasura-default-role'?: string;
  'x-hasura-allowed-customers'?: number[];
  'x-hasura-allowed-payers'?: number[];
  'x-hasura-payer-child-customers'?: number[];
  'x-hasura-allowed-regions'?: number[];
  'x-hasura-allowed-roles'?: string[];
  'x-hasura-customer-id'?: number;
  'x-hasura-user-id'?: number;
  'x-hasura-user-email'?: string;
}

export type User = {
  getToken: () => Promise<string | undefined>;
  profile: Users | null;
  claims: ConvertedClaims & { 'https://hasura.io/jwt/claims': Claims['https://hasura.io/jwt/claims'] };
};

const auth = getAuth(app);

export const buildConvenientGlobalUserObject = async (): Promise<User> => (await buildGlobalUser()).user;

/**
 * What a sign-in learned in each of its two stages, for the report when it fails:
 * - `token`: stage one, Firebase. Where the ID token came from and its timing and claim
 *   names. Never the token or a claim value.
 * - `query`: stage two, our database. The `getUserProfile` request and, when it failed, what
 *   came back. Absent when stage one gave no user id to ask with.
 */
export interface SignInDiagnostics {
  token: TokenDiagnostics;
  query?: ProfileQueryDiagnostics;
}

/** The user plus, when the profile did not load, why. Callers that act on a missing profile
 *  (sign-in, the permissions refresh) read `profileFailure` and `diagnostics` to report it.
 *  Throws `FirebaseTokenError` when Firebase could not give a token (stage one). */
export const buildGlobalUser = async (): Promise<{ user: User; profileFailure?: ProfileLoadFailure; diagnostics: SignInDiagnostics }> => {
  const claims = await getClaimsFromToken();
  const token = lastTokenDiagnostics;
  const userId = claims[siteConfig.firebaseClaimsUserId];
  const { profile, failure, query } = await loadUserProfile(userId);
  return {
    user: assembleUser(claims, profile, parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || {}),
    profileFailure: failure,
    diagnostics: { token, ...(query ? { query } : {}) },
  };
};

/** `getToken` always goes through Firebase, so a user assembled from the persisted token
 *  before the SDK finished still mints fresh tokens once it has. */
export const assembleUser = (claims: ConvertedClaims, profile: Users | null, namespaced: Claims['https://hasura.io/jwt/claims']): User => {
  const user: User = {
    getToken: getAccessTokenWithAutoRefresh,
    profile,
    claims: {
      ...claims,
      'https://hasura.io/jwt/claims': namespaced,
    },
  };
  return user;
};

// Keep a reference to the parsed token for use in the buildConvenientGlobalUserObject function
let parsedToken: IdTokenResult | null = null;

/**
 * Why a profile did not load:
 * - `no_user_id`: the token carries no `x-hasura-user-id` claim, so there was nothing to ask for.
 * - `not_found`: Hasura answered, but with no user row the token's role can see (a stale or
 *   wrong user-id claim, or a role without select on `users`).
 * - `graphql_error`: Hasura answered with an error: `code` is its `extensions.code`, such as
 *   `invalid-jwt` for an expired or unverifiable token.
 * - `http_error`: the body was not JSON (a 5xx, or a firewall or proxy page), with its status.
 * - `network_error`: the request never got an answer: offline, DNS, or the timeout.
 */
export type ProfileLoadFailure =
  | { kind: 'no_user_id' }
  | { kind: 'not_found' }
  | { kind: 'graphql_error'; code: string | null; message: string }
  | { kind: 'http_error'; status: number; contentType: string | null }
  | { kind: 'network_error'; timedOut: boolean; error: unknown };

/** The `getUserProfile` request as sent, and what came back when it failed. A successful
 *  answer is never kept: it holds the user's email and phone. */
export interface ProfileQueryDiagnostics {
  operation: 'getUserProfile';
  variables: { userId: number };
  /** The GraphQL endpoint's host, to tell production from a preview. */
  endpointHost: string | null;
  durationMs: number;
  /** Hasura's JSON answer on a GraphQL error or a missing row. */
  response?: unknown;
  /** Set when the answer was not JSON. */
  httpStatus?: number;
  bodySnippet?: string;
}

/** Stage one of sign-in, Firebase: where the ID token came from and its timing. Never the
 *  token, never a claim value. */
export interface TokenDiagnostics {
  /** `firebase` (the SDK's current user), `persisted_fallback` (the token the early boot read
   *  from storage before the SDK finished), or `none` (no signed-in user and no fallback). */
  source: 'firebase' | 'persisted_fallback' | 'none';
  issuedAt?: string;
  expiresAt?: string;
  authTime?: string;
  signInProvider?: string | null;
  claimNames: string[];
}

/** Firebase could not give an ID token (stage one of sign-in): offline, a revoked or
 *  disabled account, or the SDK's own failure. Nothing reached our database. */
export class FirebaseTokenError extends Error {
  constructor(operation: 'getIdTokenResult' | 'getIdToken(true)', cause: unknown) {
    super(`Firebase ${operation} failed: ${cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)}`);
    this.name = 'FirebaseTokenError';
    (this as { cause?: unknown }).cause = cause;
  }
}

const endpointHost = (): string | null => {
  try {
    return siteConfig.graphqlHttpUrl ? new URL(siteConfig.graphqlHttpUrl as string).host : null;
  } catch {
    return null;
  }
};

let lastTokenDiagnostics: TokenDiagnostics = { source: 'none', claimNames: [] };

/** Per-attempt timeout for the profile request. Retries, not one long wait, cover a slow line. */
export const PROFILE_REQUEST_TIMEOUT_MS = 15_000;

type ProfileResponse = {
  data?: { users_by_pk?: Users | null } | null;
  errors?: Array<{ message?: string; extensions?: { code?: string } }>;
};

/** A codegen'd document's text (`__text`), or a `gql`-tag document's `loc.source.body`. */
const documentText = (doc: unknown): string | undefined => {
  const d = doc as { __text?: string; loc?: { source?: { body?: string } } } | null | undefined;
  return d?.__text ?? d?.loc?.source?.body;
};

/**
 * The profile, or why it did not load. Never throws.
 *
 * Sends the query as text so `execGraphqlWithFetch` returns the whole response: given the
 * document, it reads `data.users_by_pk` and throws a TypeError when Hasura answered with
 * `errors`, which loses Hasura's message.
 */
export const loadUserProfile = async (
  userId?: number,
  token?: string
): Promise<{ profile: Users | null; failure?: ProfileLoadFailure; query?: ProfileQueryDiagnostics }> => {
  if (!userId) return { profile: null, failure: { kind: 'no_user_id' } };
  const startedAt = Date.now();
  const query = (extra: Partial<ProfileQueryDiagnostics> = {}): ProfileQueryDiagnostics => ({
    operation: 'getUserProfile',
    variables: { userId },
    endpointHost: endpointHost(),
    durationMs: Date.now() - startedAt,
    ...extra,
  });
  try {
    const json = await execGraphqlWithFetch<ProfileResponse>({
      query: documentText(GetUserProfileDocument) ?? (GetUserProfileDocument as never),
      variables: { userId },
      token,
      options: { cache: 'no-store', timeout: PROFILE_REQUEST_TIMEOUT_MS } as RequestInit,
    });

    if (json?.errors?.length) {
      const first = json.errors[0];
      return {
        profile: null,
        failure: { kind: 'graphql_error', code: first?.extensions?.code ?? null, message: first?.message ?? 'GraphQL error' },
        // Hasura's whole answer: `errors` (message, code, path) and `data`, which is null here.
        query: query({ response: { errors: json.errors, data: json.data ?? null } }),
      };
    }

    const user = json?.data?.users_by_pk;
    if (user?.id) {
      // A grant whose feature the role cannot select arrives with a null
      // feature and counts as no access rather than failing the profile fetch.
      setPermissions(grantedFeatureNames((user as any).users_usertofeatures));
      return { profile: user };
    }

    // The answer had no row; it carries no personal data, so it is kept whole.
    return { profile: null, failure: { kind: 'not_found' }, query: query({ response: json ?? null }) };
  } catch (err) {
    console.log('Error getting user', err);
    if (err instanceof HttpResponseError) {
      return {
        profile: null,
        failure: { kind: 'http_error', status: err.status, contentType: err.contentType },
        query: query({ httpStatus: err.status, bodySnippet: err.bodySnippet }),
      };
    }
    const timedOut = err instanceof Error && err.name === 'AbortError';
    return { profile: null, failure: { kind: 'network_error', timedOut, error: err }, query: query() };
  }
};

export const getUserProfile = async (userId?: number, token?: string): Promise<Users | null> =>
  (await loadUserProfile(userId, token)).profile;

export const getUserProfileLastUpdatedAt = async (userId: number): Promise<Users_Select_Column.UpdatedAt | null> => {
  try {
    const user = await execGraphqlWithFetch<Users>({
      query: GetUserProfileUpdatedAtDocument,
      variables: { userId },
    });

    if ((user as Users).updated_at) {
      return user?.updated_at as Users_Select_Column.UpdatedAt;
    }

    return null;
  } catch (err) {
    console.log('Error getting user', err);
    return null;
  }
};

export const getClaimsFromToken = async (): Promise<ConvertedClaims> => {
  let tokenResult: IdTokenResult | undefined;
  try {
    tokenResult = await auth.currentUser?.getIdTokenResult();
  } catch (err) {
    throw new FirebaseTokenError('getIdTokenResult', err);
  }
  parsedToken = tokenResult || null;
  if (!parsedToken && persistedFallback) {
    lastTokenDiagnostics = { source: 'persisted_fallback', claimNames: Object.keys(persistedFallback.namespaced ?? {}).sort() };
    return convertRawClaims(persistedFallback.namespaced);
  }
  const namespaced = parsedToken?.claims[siteConfig.firebaseClaimsNamespace] || {};
  lastTokenDiagnostics = parsedToken
    ? {
        source: 'firebase',
        issuedAt: parsedToken.issuedAtTime,
        expiresAt: parsedToken.expirationTime,
        authTime: parsedToken.authTime,
        signInProvider: parsedToken.signInProvider,
        claimNames: Object.keys(namespaced as object).sort(),
      }
    : { source: 'none', claimNames: [] };
  return convertRawClaims(namespaced);
};

/** Parses Hasura's array literals and makes the customer id a number. Shared by the token
 *  path above and the persisted-session boot (features/auth/earlySession.ts), so both
 *  produce the same `claims` shape. */
export const convertRawClaims = (namespaced: unknown): ConvertedClaims => {
  const rawClaims = JSON.parse(JSON.stringify(namespaced || {}));

  if (rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    rawClaims[siteConfig.firebaseClaimsAllowedRegions] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedRegions])
    );

  if (rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    rawClaims[siteConfig.firebaseClaimsPayerChildCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsPayerChildCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    rawClaims[siteConfig.firebaseClaimsAllowedCustomers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedCustomers])
    );

  if (rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    rawClaims[siteConfig.firebaseClaimsAllowedPayers] = JSON.parse(
      getArrayFromHasuraArray(rawClaims[siteConfig.firebaseClaimsAllowedPayers])
    );

  if (
    rawClaims[siteConfig.firebaseClaimsCustomerId] &&
    !isNaN(Number(rawClaims[siteConfig.firebaseClaimsCustomerId]))
  ) {
    rawClaims[siteConfig.firebaseClaimsCustomerId] = parseInt(rawClaims[siteConfig.firebaseClaimsCustomerId]);
  }

  return rawClaims;
};

export const checkForPermittedRole = (user?: { claims?: ConvertedClaims }) => {
  const defaultRole = user?.claims?.['x-hasura-default-role'] || ``;
  return (
    defaultRole === `admin` ||
    defaultRole === `dispatcher` ||
    defaultRole === `dealer-super-admin` ||
    defaultRole === `dealer-admin` ||
    defaultRole === `dealer` ||
    defaultRole === `program-admin`
  );
};

export const checkForPowerRole = (user?: { claims?: ConvertedClaims }) => {
  const defaultRole = user?.claims?.['x-hasura-default-role'] || ``;
  return (
    defaultRole === `admin` ||
    defaultRole === `dispatcher` ||
    defaultRole === `dealer-super-admin` ||
    defaultRole === `dealer-admin` ||
    defaultRole === `program-admin`
  );
};

const getArrayFromHasuraArray = (arr: string) => {
  try {
    const replacedJSON = JSON.stringify(arr).replace('{', '[').replace('}', ']');
    const parsedJSON = JSON.parse(replacedJSON);
    return parsedJSON;
  } catch (error) {
    console.log(`Failed to convert Hasura array`, { hasuraArray: arr, error });
    return;
  }
};

const login = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password);
};

const logout = () => {
  return signOut(auth);
};

/** The signed-in Firebase uid, or undefined when nobody is signed in. */
const currentUid = (): string | undefined => auth.currentUser?.uid;

/** Ask Firebase for a fresh ID token, so the next claims read sees what the server holds now. */
const refreshIdToken = async (): Promise<void> => {
  try {
    await auth.currentUser?.getIdToken(true);
  } catch (err) {
    throw new FirebaseTokenError('getIdToken(true)', err);
  }
};

const resetPassword = (email: string) => {
  return sendPasswordResetEmail(auth, email);
};

const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await firebaseUpdatePassword(user, newPassword);
};

const verifyPasswordResetCode = (oobCode: string) => {
  return firebaseVerifyPasswordResetCode(auth, oobCode);
};

const confirmPasswordReset = (oobCode: string, newPassword: string) => {
  return firebaseConfirmPasswordReset(auth, oobCode, newPassword);
};

const onAuthStateChanged = (callback: (user: FirebaseUser | null) => void) => {
  return firebaseOnAuthStateChanged(auth, callback);
};

const onIdTokenChanged = (callback: (user: FirebaseUser | null) => void) => {
  return firebaseOnIdTokenChanged(auth, callback);
};

const updateUserActivity = async (userId: number): Promise<void> => {
  try {
    await execGraphqlWithFetch({
      query: UpdateUserActivityDocument,
      variables: { userId },
    });
  } catch (err) {
    console.error('Failed to update user activity:', err);
  }
};

/**
 * The token Firebase persisted in this browser, usable until the SDK has finished
 * starting and `auth.currentUser` exists. Set by the persisted-session boot,
 * consulted only when the SDK has no current user, cleared on sign-out. While it is in
 * use, every request still carries it to Hasura, which verifies it.
 */
let persistedFallback: { token: string; namespaced: Claims['https://hasura.io/jwt/claims'] } | undefined;

export const setPersistedTokenFallback = (token: string, namespaced: Claims['https://hasura.io/jwt/claims']): void => {
  persistedFallback = { token, namespaced };
};

export const clearPersistedTokenFallback = (): void => {
  persistedFallback = undefined;
};

const getAccessTokenWithAutoRefresh = async (): Promise<string | undefined> => {
  try {
    let firebaseTokenResult = null;
    if (auth && auth.currentUser) {
      firebaseTokenResult = await auth.currentUser.getIdTokenResult();
    }
    return firebaseTokenResult?.token ?? persistedFallback?.token;
  } catch (err) {
    console.log('Error getting most up-to-date token', err);
    return;
  }
};

const getAuthStatus = async () => {
  if (auth && auth.currentUser) {
    return true;
  } else return Boolean(persistedFallback);
};

const getAccessToken = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token ?? persistedFallback?.token;
  } catch (err) {
    console.log('Error getting token', err);
    return;
  }
};

export type TokenInfo =
  | { available: false; error?: string }
  | {
      available: true;
      iat: number;
      exp: number;
      tokenAgeSec: number;
      tokenExpiresInSec: number;
      tokenExpired: boolean;
    };

/**
 * Returns parsed expiration metadata for the current Firebase ID token.
 * Calls getIdTokenResult() which auto-refreshes if the cached token has
 * expired, so a small tokenAgeSec paired with a downstream JWTExpired
 * error indicates the prior token expired mid-flow.
 */
export const getTokenInfo = async (): Promise<TokenInfo> => {
  try {
    if (!auth?.currentUser) return { available: false };
    const result = await auth.currentUser.getIdTokenResult();
    if (!result) return { available: false };
    const exp = Math.floor(new Date(result.expirationTime).getTime() / 1000);
    const iat = Math.floor(new Date(result.issuedAtTime).getTime() / 1000);
    const now = Math.floor(Date.now() / 1000);
    return {
      available: true,
      iat,
      exp,
      tokenAgeSec: now - iat,
      tokenExpiresInSec: exp - now,
      tokenExpired: exp <= now,
    };
  } catch (err: any) {
    return { available: false, error: err?.message };
  }
};

const writeUserEvent = async (action: string) => {
  console.log('user eventlog action', action);
};

const authService = {
  login,
  logout,
  refreshIdToken,
  currentUid,
  resetPassword,
  changePassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  onAuthStateChanged,
  onIdTokenChanged,
  updateUserActivity,
  getAccessTokenWithAutoRefresh,
  getAccessToken,
  getAuthStatus,
  writeUserEvent,
};

export { auth, authService };

export default authService;
